import React, { useMemo, useState } from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { SearchIcon, LandmarkIcon, HeartIcon, StethoscopeIcon, WalletIcon, ArrowRightIcon, ShieldIcon, DownloadIcon, FileTextIcon, SearchXIcon, PhoneIcon, ExternalLinkIcon } from 'lucide-react';
// Resource Data
const resourcesData = [
// Government & Health
{
  id: 1,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'Healthfinders.gov',
  desc: 'Find information to help you and your loved ones stay healthy.',
  url: 'https://healthfinder.gov'
}, {
  id: 2,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'Maryland Nurses Association',
  desc: 'The only state-wide, non-profit, multipurpose professional membership organization for Registered Nurses in Maryland.',
  url: 'https://www.marylandrn.org'
}, {
  id: 3,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'Maryland Healthcare Commission',
  desc: 'An independent regulatory agency whose mission is to plan for health system needs, promote informed decision-making, increase accountability, and improve access.',
  url: 'https://mhcc.maryland.gov'
}, {
  id: 4,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'National Institute of Health Senior Health',
  desc: 'Health and wellness information for older adults from the National Institutes of Health.',
  url: 'https://www.nih.gov'
}, {
  id: 5,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'World Health Organization',
  desc: 'The United Nations public health arm. Monitors disease outbreaks, assesses the performance of health systems around the globe.',
  url: 'https://www.who.int'
}, {
  id: 6,
  category: 'Government & Health',
  icon: LandmarkIcon,
  colorClass: 'bg-blue-100 text-blue-600',
  title: 'National Institute on Aging Health Information',
  desc: 'Comprehensive health information and resources specifically designed for aging populations.',
  url: 'https://www.nia.nih.gov/health'
},
// Caregiving Support
{
  id: 7,
  category: 'Caregiving Support',
  icon: HeartIcon,
  colorClass: 'bg-rose-100 text-rose-600',
  title: 'Family Caregiver Alliance',
  desc: 'Provides a wealth of information on care strategies, stress relief, community resources, family issues and hands-on care.',
  url: 'https://www.caregiver.org'
}, {
  id: 8,
  category: 'Caregiving Support',
  icon: HeartIcon,
  colorClass: 'bg-rose-100 text-rose-600',
  title: 'Caring from a Distance (CFAD)',
  desc: 'A nonprofit organization offering assistance, resources and support for men and women providing long distance care.',
  url: 'https://www.cfad.org'
}, {
  id: 9,
  category: 'Caregiving Support',
  icon: HeartIcon,
  colorClass: 'bg-rose-100 text-rose-600',
  title: 'National Family Caregivers Association',
  desc: 'Supporting and empowering family caregivers across the nation with education, resources, and advocacy.',
  phone: '1-800-896-3650',
  url: '#'
}, {
  id: 10,
  category: 'Caregiving Support',
  icon: HeartIcon,
  colorClass: 'bg-rose-100 text-rose-600',
  title: 'National Association of Area Agency on Aging (N4A)',
  desc: 'Builds the capacity of its members to help older persons and persons with disabilities live with dignity and choices in their homes and communities.',
  url: 'https://www.n4a.org'
}, {
  id: 11,
  category: 'Caregiving Support',
  icon: HeartIcon,
  colorClass: 'bg-rose-100 text-rose-600',
  title: 'Eldercare Locator',
  desc: 'A nation-wide resource providing information on locating a variety of community services that supports older adults.',
  phone: '1-800-677-1116 (Mon-Fri, 9am-8pm ET)',
  url: 'https://eldercare.acl.gov'
},
// Health Organizations
{
  id: 12,
  category: 'Health Organizations',
  icon: StethoscopeIcon,
  colorClass: 'bg-emerald-100 text-emerald-600',
  title: 'AARP Internet Resources',
  desc: 'Provides a frequently updated database of Internet resources, including links to more than 900 of the best sites for people age 50+.',
  url: 'https://www.aarp.org'
}, {
  id: 13,
  category: 'Health Organizations',
  icon: StethoscopeIcon,
  colorClass: 'bg-emerald-100 text-emerald-600',
  title: "The Alzheimer's Association",
  desc: 'The leading voluntary health organization in Alzheimer care, support and research.',
  url: 'https://www.alz.org'
}, {
  id: 14,
  category: 'Health Organizations',
  icon: StethoscopeIcon,
  colorClass: 'bg-emerald-100 text-emerald-600',
  title: 'The American Cancer Society',
  desc: 'Provides extensive information on preventing cancer and diminishing suffering from cancer, through research, education, advocacy, and service.',
  url: 'https://www.cancer.org'
}, {
  id: 15,
  category: 'Health Organizations',
  icon: StethoscopeIcon,
  colorClass: 'bg-emerald-100 text-emerald-600',
  title: 'National Private Duty Association',
  desc: 'The national association representing the private duty home care industry with resources for providers and consumers.',
  url: 'https://www.privatedutyhomecare.org'
},
// Financial Assistance
{
  id: 16,
  category: 'Financial Assistance',
  icon: WalletIcon,
  colorClass: 'bg-amber-100 text-amber-600',
  title: 'Benefits Checkup (National Council on Aging)',
  desc: 'Helps find programs for people ages 55 and over that may pay for some of their costs of prescription drugs, health care, utilities, and other essential items or services.',
  url: 'https://www.benefitscheckup.org'
}, {
  id: 17,
  category: 'Financial Assistance',
  icon: WalletIcon,
  colorClass: 'bg-amber-100 text-amber-600',
  title: 'NIH Senior Health',
  desc: 'Provides an easy-to-navigate menu of articles about aging and health-related topics for seniors and their families.',
  url: 'https://nihseniorhealth.gov'
}];
const categories = ['All', 'Government & Health', 'Caregiving Support', 'Health Organizations', 'Financial Assistance'];
export function ResourcesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useScrollAnimation();
  const {
    ref: sidebarRef,
    isVisible: isSidebarVisible
  } = useScrollAnimation();
  // Filter resources based on search and category
  const filteredResources = useMemo(() => {
    return resourcesData.filter(resource => {
      const matchesCategory = activeCategory === 'All' || resource.category === activeCategory;
      const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) || resource.desc.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);
  const handleCategoryClick = (category: string) => {
    setActiveCategory(prev => prev === category ? 'All' : category);
  };
  return <main className="bg-light min-h-screen" data-id="element-1150">
      <PageBanner title="Resources & Support" subtitle="Trusted information and organizations for seniors, families, and caregivers" breadcrumbs={[{
      label: 'Resources',
      path: '/resources'
    }]} data-id="element-1151" />

      {/* Intro & Search Section */}
      <section className="py-12 bg-gray-50 border-b border-gray-200" data-id="element-1152">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1153">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-1154">
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-6 mb-10 max-w-4xl mx-auto text-center" data-id="element-1155">
              <p className="text-dark-muted leading-relaxed" data-id="element-1156">
                Listed below are links to many helpful organizations and
                resources for seniors, their families and caregivers. These
                listings are for informational purposes only and do not
                represent an endorsement by (or for) Edvin Home Healthcare
                Solutions.
              </p>
            </div>

            <div className="max-w-4xl mx-auto" data-id="element-1157">
              {/* Search Bar */}
              <div className="relative mb-8" data-id="element-1158">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1159">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1160" />
                </div>
                <input type="text" className="block w-full pl-11 pr-4 py-4 border border-gray-200 rounded-xl leading-5 bg-light-alt placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-lg shadow-sm transition-all" placeholder="Search resources..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1161" />
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap justify-center gap-3" data-id="element-1162">
                {categories.map(category => <button key={category} onClick={() => handleCategoryClick(category)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${activeCategory === category ? 'bg-primary text-white shadow-md' : 'bg-light-alt text-dark-muted border border-gray-200 hover:border-primary hover:text-primary'}`} data-id="element-1163">
                    {category}
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resource Cards Grid */}
      <section className="py-16 bg-white" data-id="element-1164">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1165">
          <div ref={gridRef} className={`transition-all duration-1000 ease-out delay-100 ${isGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1166">
            {filteredResources.length === 0 ? <div className="text-center py-20 bg-light rounded-2xl border border-gray-200 shadow-sm" data-id="element-1167">
                <SearchXIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-1168" />
                <h3 className="text-xl font-bold text-dark mb-2" data-id="element-1169">
                  No resources found
                </h3>
                <p className="text-dark-muted" data-id="element-1170">
                  Try adjusting your search or category filter.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('All');
            }} className="mt-6 text-primary font-semibold hover:underline" data-id="element-1171">
                  Clear all filters
                </button>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1172">
                {filteredResources.map(resource => <div key={resource.id} className="bg-white rounded-xl p-6 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full group" data-id="element-1173">
                    <div className="flex items-start mb-4" data-id="element-1174">
                      <div className={`p-3 rounded-xl mr-4 flex-shrink-0 ${resource.colorClass}`} data-id="element-1175">
                        <resource.icon className="h-6 w-6" data-id="element-1176" />
                      </div>
                      <div data-id="element-1177">
                        {activeCategory === 'All' && <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 block mb-1" data-id="element-1178">
                            {resource.category}
                          </span>}
                        <h3 className="text-lg font-bold text-dark leading-tight group-hover:text-primary transition-colors" data-id="element-1179">
                          {resource.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-dark-muted text-sm mb-6 flex-grow leading-relaxed" data-id="element-1180">
                      {resource.desc}
                    </p>

                    <div className="mt-auto pt-4 border-t border-gray-50 space-y-3" data-id="element-1181">
                      {resource.phone && <div className="flex items-center text-primary font-semibold text-sm" data-id="element-1182">
                          <PhoneIcon className="h-4 w-4 mr-2" data-id="element-1183" />
                          {resource.phone}
                        </div>}

                      <a href={resource.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary font-semibold text-sm hover:text-primary-dark hover:underline transition-colors" data-id="element-1184">
                        Visit Website{' '}
                        <ArrowRightIcon className="ml-1.5 h-4 w-4" data-id="element-1185" />
                      </a>
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>
      </section>

      {/* Sidebar Modules (Bottom Grid) */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-1186">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1187">
          <div ref={sidebarRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-200 ${isSidebarVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1188">
            {/* Module 1: Insurance Options */}
            <div className="bg-primary-dark text-white rounded-2xl p-8 shadow-xl flex flex-col" data-id="element-1189">
              <ShieldIcon className="h-12 w-12 text-secondary mb-6" data-id="element-1190" />
              <h3 className="text-2xl font-bold mb-4" data-id="element-1191">
                Insurance & Funding Options
              </h3>
              <p className="text-white/90 mb-8 leading-relaxed flex-grow" data-id="element-1192">
                See which public, private and community programs meet your
                needs. We help navigate insurance, Medicare, Medicaid, and
                community programs to ensure you receive the care you deserve.
              </p>
              <div className="space-y-4 mt-auto" data-id="element-1193">
                <a href="https://www.healthcare.gov" target="_blank" rel="noopener noreferrer" className="block w-full bg-white text-primary-dark px-6 py-3 rounded-lg font-bold text-center hover:bg-light-alt transition-colors shadow-md" data-id="element-1194">
                  Learn About Insurance Options
                </a>
                <a href="#/contact" className="block w-full text-center text-white/80 hover:text-white font-medium transition-colors" data-id="element-1195">
                  Contact us to discuss coverage
                </a>
              </div>
            </div>

            {/* Module 2: Featured Partners */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 flex flex-col" data-id="element-1196">
              <h3 className="text-xl font-bold text-dark mb-6 flex items-center" data-id="element-1197">
                <HeartIcon className="h-6 w-6 text-primary mr-2" data-id="element-1198" />
                Trusted Partners
              </h3>
              <div className="space-y-4 flex-grow" data-id="element-1199">
                <a href="https://www.aarp.org" target="_blank" rel="noopener noreferrer" className="block bg-light p-4 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all group" data-id="element-1200">
                  <h4 className="font-bold text-dark group-hover:text-primary transition-colors" data-id="element-1201">
                    AARP
                  </h4>
                  <p className="text-sm text-dark-muted mb-2" data-id="element-1202">
                    Advocacy & Resources for 50+
                  </p>
                  <span className="text-xs font-semibold text-primary flex items-center" data-id="element-1203">
                    Visit <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-1204" />
                  </span>
                </a>
                <a href="https://www.alz.org" target="_blank" rel="noopener noreferrer" className="block bg-light p-4 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all group" data-id="element-1205">
                  <h4 className="font-bold text-dark group-hover:text-primary transition-colors" data-id="element-1206">
                    Alzheimer's Association
                  </h4>
                  <p className="text-sm text-dark-muted mb-2" data-id="element-1207">
                    Care, Support & Research
                  </p>
                  <span className="text-xs font-semibold text-primary flex items-center" data-id="element-1208">
                    Visit <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-1209" />
                  </span>
                </a>
                <a href="https://www.cancer.org" target="_blank" rel="noopener noreferrer" className="block bg-light p-4 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all group" data-id="element-1210">
                  <h4 className="font-bold text-dark group-hover:text-primary transition-colors" data-id="element-1211">
                    American Cancer Society
                  </h4>
                  <p className="text-sm text-dark-muted mb-2" data-id="element-1212">
                    Research, Education & Advocacy
                  </p>
                  <span className="text-xs font-semibold text-primary flex items-center" data-id="element-1213">
                    Visit <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-1214" />
                  </span>
                </a>
              </div>
            </div>

            {/* Module 3: Downloads */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 flex flex-col" data-id="element-1215">
              <DownloadIcon className="h-12 w-12 text-primary mb-6" data-id="element-1216" />
              <h3 className="text-xl font-bold text-dark mb-6" data-id="element-1217">Downloads</h3>
              <div className="space-y-4 flex-grow" data-id="element-1218">
                <div className="bg-light p-5 rounded-xl border border-gray-200 flex flex-col h-full justify-between" data-id="element-1219">
                  <div className="flex items-start mb-4" data-id="element-1220">
                    <FileTextIcon className="h-6 w-6 text-primary mr-3 flex-shrink-0" data-id="element-1221" />
                    <h4 className="font-bold text-dark" data-id="element-1222">
                      Edvin Home Health Care Solution and Nursing Services
                      Brochure
                    </h4>
                  </div>
                  <p className="text-sm text-dark-muted italic" data-id="element-1223">
                    Note: The links provided above are for informational
                    purposes only. They do not constitute medical advice and do
                    not represent an endorsement by (or for) Edvin Home Health
                    Care Solution and Nursing Services.
                  </p>
                </div>
                <div className="bg-light p-5 rounded-xl border border-gray-200 flex flex-col h-full justify-between" data-id="element-1224">
                  <div className="flex items-start mb-4" data-id="element-1225">
                    <FileTextIcon className="h-6 w-6 text-primary mr-3 flex-shrink-0" data-id="element-1226" />
                    <h4 className="font-bold text-dark" data-id="element-1227">
                      Employment Application
                    </h4>
                  </div>
                  <a href="#" className="inline-block w-full text-center border-2 border-primary text-primary hover:bg-primary hover:text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm" data-id="element-1228">
                    Download PDF
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1229" />
    </main>;
}